<template>
<BaseTemplate app-name="RIS System" page-title="">
  <template #content>
    <div
      class="h-screen flex flex-col bg-gray-50 dark:bg-gray-900 text-gray-800 dark:text-gray-100 transition-colors duration-200"
    >
      <!-- Main Content -->
      <div class="flex-1 flex overflow-hidden">
        <!-- Content Area -->
        <div class="flex-1 overflow-hidden flex flex-col">
          <!-- Header -->
          <div class="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 p-4 shadow-sm">
            <div class="flex flex-wrap justify-between items-center gap-4">
              <div class="flex items-center gap-3">
                <h2 class="text-xl font-medium" :style="{ color: 'var(--clinic-font-color)' }">{{ currentView === 'list' ? 'Médicos Cadastrados' : 'Cadastro de Médico' }}</h2>
                <button
                  v-if="currentView === 'form'"
                  class="w-9 h-9 flex items-center justify-center rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  @click="goToList"
                  aria-label="Voltar para lista"
                >
                  <ArrowLeftIcon class="w-5 h-5" />
                </button>
              </div>
              <div class="flex items-center gap-2">
                <button
                  v-if="currentView === 'list'"
                  class="clinic-btn text-white rounded-md transition-colors shadow-sm flex items-center gap-2"
                  @click="openNewDoctorForm"
                >
                  <PlusIcon class="w-4 h-4" />
                  <span>Novo Médico</span>
                </button>
                <button
                  v-if="currentView === 'form'"
                  class="clinic-btn text-white rounded-md transition-colors shadow-sm flex items-center gap-2"
                  @click="saveDoctor"
                  :disabled="!isFormValid || isSaving"
                >
                  <SaveIcon class="w-4 h-4" />
                  <span>{{ isSaving ? 'Salvando...' : (editMode ? 'Atualizar' : 'Salvar') }} Médico</span>
                </button>
              </div>
            </div>
          </div>

          <!-- Doctor List View -->
          <div v-if="currentView === 'list'" class="flex-1 overflow-y-auto p-4 bg-gray-50 dark:bg-gray-900">
            <div class="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-300 dark:border-gray-700 p-4">
              <!-- Search and Filter -->
              <div class="mb-4 flex flex-wrap gap-4 items-center">
                <div class="flex-1 min-w-[240px]">
                  <div class="relative">
                    <input
                      type="text"
                      v-model="searchQuery"
                      placeholder="Buscar médicos..."
                      @input="debouncedSearch"
                      class="h-10 w-full px-3 pl-10 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <SearchIcon class="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  </div>
                </div>

                <div class="relative">
                  <select
                    v-model="filterStatus"
                    @change="loadDoctors(1)"
                    class="h-10 px-3 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:ring-2 focus:ring-blue-500 appearance-none pr-8"
                  >
                    <option value="">Todos os status</option>
                    <option value="true">Ativos</option>
                    <option value="false">Inativos</option>
                  </select>
                  <ChevronDownIcon class="w-4 h-4 text-gray-500 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />
                </div>
                

                <div class="relative">
                  <select
                    v-model="filterSpecialty"
                    @change="loadDoctors(1)"
                    class="h-10 px-3 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:ring-2 focus:ring-blue-500 appearance-none pr-8"
                  >
                    <option value="">Todas as CBOS / Especialidades</option>
                    <option v-for="specialty in specialties" :key="specialty" :value="specialty">
                      {{ specialty }}
                    </option>
                  </select>
                  <ChevronDownIcon class="w-4 h-4 text-gray-500 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />
                </div>

                <button
                  @click="resetFilters"
                  class="h-10 px-3 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm hover:bg-gray-50 dark:hover:bg-gray-800 focus:ring-2 focus:ring-blue-500"
                >
                  <RefreshCwIcon class="w-4 h-4 inline-block" />
                </button>
              </div>

              <!-- Error Message -->
              <div v-if="errorMessage" class="mb-4 p-4 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-700 rounded-md">
                <p class="text-red-800 dark:text-red-400 text-sm">{{ errorMessage }}</p>
              </div>

              <!-- Loading Overlay -->
              <div v-if="isLoading" class="py-10 flex justify-center items-center">
                <LoaderIcon class="w-12 h-12 animate-spin text-gray-500 dark:text-gray-400" />
              </div>

              <!-- Doctor Table -->
              <div v-else class="overflow-x-auto">
                <table class="w-full border-collapse">
                  <thead>
                    <tr class="bg-gray-50 dark:bg-gray-900/50 text-left">
                      <th class="px-4 py-3 text-sm font-medium text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                        Nome
                      </th>
                      <th class="px-4 py-3 text-sm font-medium text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                        CRM
                      </th>
                      <th class="px-4 py-3 text-sm font-medium text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                       Especialidade
                      </th>
                      <th class="px-4 py-3 text-sm font-medium text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                        Número CBO
                      </th>
                      <th class="px-4 py-3 text-sm font-medium text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                        Telefone
                      </th>
                      <th class="px-4 py-3 text-sm font-medium text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                        Status
                      </th>
                      <th class="px-4 py-3 text-sm font-medium text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                        Ações
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-if="doctors.length === 0">
                      <td colspan="7" class="px-4 py-6 text-center text-gray-500 dark:text-gray-400">
                        Nenhum médico encontrado
                      </td>
                    </tr>
                    <tr
                      v-for="doctor in doctors"
                      :key="doctor.id"
                      class="border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                    >
                      <td class="px-4 py-3">
                        <div class="text-sm font-medium">{{ doctor.name }}</div>
                        <div class="text-xs text-gray-500 dark:text-gray-400">{{ doctor.email }}</div>
                      </td>
                      <td class="px-4 py-3 text-sm">{{ formatCRMDisplay(doctor.crm) }}</td>
                      <td class="px-4 py-3 text-sm">{{ doctor.specialty }}</td>
                      <td class="px-4 py-3 text-sm">{{ doctor.cbo || 'Não informado' }}</td>
                      <td class="px-4 py-3 text-sm">{{ doctor.phone || 'Não informado' }}</td>
                      <td class="px-4 py-3 text-sm">
                        <span
                          class="px-2 py-1 text-xs rounded-full"
                          :class="{
                            'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400': doctor.is_active,
                            'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-400': !doctor.is_active
                          }"
                        >
                          {{ doctor.is_active ? 'Ativo' : 'Inativo' }}
                        </span>
                      </td>
                      <td class="px-4 py-3 text-sm">
                        <div class="flex gap-2">
                          <button
                            @click="editDoctor(doctor)"
                            class="p-1 text-gray-500 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400"
                            title="Editar"
                          >
                            <EditIcon class="w-4 h-4" />
                          </button>
                          <button
                            @click="confirmDeleteDoctor(doctor)"
                            class="p-1 text-gray-500 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400"
                            title="Excluir"
                          >
                            <TrashIcon class="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>

                <!-- Pagination -->
                <div class="flex flex-col md:flex-row md:items-center md:justify-between my-4 gap-2">
                  <div class="flex items-center gap-2 justify-center">
                    <button
                      :disabled="currentPage === 1"
                      @click="loadDoctors(currentPage - 1)"
                      class="px-3 py-1 rounded bg-emerald-500 dark:bg-gray-700 text-gray-200 dark:text-gray-200 disabled:opacity-50"
                    >
                      Anterior
                    </button>
                    <span class="text-sm text-gray-700 dark:text-gray-200">
                      Página {{ currentPage }} de {{ totalPages }}
                    </span>
                    <button
                      :disabled="currentPage === totalPages || totalPages === 0"
                      @click="loadDoctors(currentPage + 1)"
                      class="px-3 py-1 rounded bg-emerald-500 dark:bg-gray-700 text-gray-200 dark:text-gray-200 disabled:opacity-50"
                    >
                      Próxima
                    </button>
                  </div>
                  <div class="flex items-center gap-2 justify-center">
                    <label for="page-size" class="text-sm text-gray-700 dark:text-gray-200">Itens por página:</label>
                    <select
                      v-model.number="pageSize"
                      @change="loadDoctors(1)"
                      class="rounded border px-2 py-1 text-sm dark:bg-gray-700 dark:text-white"
                    >
                      <option :value="5">5</option>
                      <option :value="10">10</option>
                      <option :value="20">20</option>
                      <option :value="50">50</option>
                    </select>
                    <span class="text-xs text-gray-500 dark:text-gray-400">Total: {{ totalCount }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Doctor Form View with Tabs -->
          <div v-if="currentView === 'form'" class="flex-1 overflow-y-auto p-4 bg-gray-50 dark:bg-gray-900">
            <div class="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-300 dark:border-gray-700">
              <!-- Tabs Navigation -->
              <div class="border-b border-gray-200 dark:border-gray-700">
                <div class="flex overflow-x-auto">
                  <button
                    v-for="tab in tabs"
                    :key="tab.id"
                    @click="activeTab = tab.id"
                    class="px-4 py-3 text-sm font-medium whitespace-nowrap transition-colors focus:outline-none"
                    :style="{
                      color: activeTab === tab.id ? 'var(--clinic-icon-color)' : 'var(--clinic-font-color)',
                      borderBottom: activeTab === tab.id ? '2px solid var(--clinic-icon-color)' : '2px solid transparent'
                    }"
                  >
                    <div class="flex items-center gap-2">
                      <component :is="tab.icon" class="w-4 h-4" />
                      {{ tab.name }}
                    </div>
                  </button>
                </div>
              </div>

              <!-- Tab Content -->
              <div class="p-6">
                <!-- ABA: Informações Pessoais -->
                <div v-if="activeTab === 'personal'" class="space-y-6">
                  <div>
                    <h3 class="text-lg font-medium mb-4 text-gray-900 dark:text-white">Informações Pessoais</h3>
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div class="md:col-span-2">
                        <label for="name" class="block text-sm font-medium mb-1">Nome Completo*</label>
                        <input
                          id="name"
                          type="text"
                          v-model="currentDoctor.name"
                          required
                          :class="[
                            'w-full px-3 py-2 border rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:border-transparent transition-all',
                            formErrors.name ? 'border-red-500 ring-2 ring-red-500 ring-opacity-50 bg-red-50 dark:bg-red-900/20' : 'border-gray-200 dark:border-gray-700 focus:ring-blue-500'
                          ]"
                          @blur="validateField('name')"
                        />
                        
                        <p v-if="formErrors.name" class="mt-1 text-sm text-red-600 dark:text-red-400 font-medium">{{ formErrors.name }}</p>
                      </div>
                      
                      <div>
                        <label for="email" class="block text-sm font-medium mb-1">E-mail*</label>
                        <input
                          id="email"
                          type="email"
                          v-model="currentDoctor.email"
                          required
                          :class="[
                            'w-full px-3 py-2 border rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:border-transparent transition-all',
                            formErrors.email ? 'border-red-500 ring-2 ring-red-500 ring-opacity-50 bg-red-50 dark:bg-red-900/20' : 'border-gray-200 dark:border-gray-700 focus:ring-blue-500'
                          ]"
                        />
                        <p v-if="formErrors.email" class="mt-1 text-sm text-red-600 dark:text-red-400 font-medium">{{ formErrors.email }}</p>
                      </div>
                      
                      <div>
                        <label for="phone" class="block text-sm font-medium mb-1">Telefone</label>
                        <input
                          id="phone"
                          type="tel"
                          v-model="currentDoctor.phone"
                          :class="[
                            'w-full px-3 py-2 border rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent',
                            formErrors.phone ? 'border-red-500' : 'border-gray-200 dark:border-gray-700'
                          ]"
                        />
                        <p v-if="formErrors.phone" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ formErrors.phone }}</p>
                      </div>
                      
                      <div>
                        <label for="birth_date" class="block text-sm font-medium mb-1">Data de Nascimento</label>
                        <input
                          id="birth_date"
                          type="date"
                          v-model="currentDoctor.birth_date"
                          :class="[
                            'w-full px-3 py-2 border rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent',
                            formErrors.birth_date ? 'border-red-500' : 'border-gray-200 dark:border-gray-700'
                          ]"
                        />
                        <p v-if="formErrors.birth_date" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ formErrors.birth_date }}</p>
                      </div>
                      
                      <div>
                        <label for="cpf" class="block text-sm font-medium mb-1">CPF</label>
                        <input
                          id="cpf"
                          type="text"
                          v-model="currentDoctor.cpf"
                          :class="[
                            'w-full px-3 py-2 border rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:border-transparent transition-all',
                            formErrors.cpf ? 'border-red-500 ring-2 ring-red-500 ring-opacity-50 bg-red-50 dark:bg-red-900/20' : 'border-gray-200 dark:border-gray-700 focus:ring-blue-500'
                          ]"
                          @blur="validateField('cpf')"
                        />
                        <p v-if="formErrors.cpf" class="mt-1 text-sm text-red-600 dark:text-red-400 font-medium">{{ formErrors.cpf }}</p>
                      </div>
                      
                      <div>
                        <label for="cns" class="block text-sm font-medium mb-1">CNS (Cartão Nacional de Saúde)*</label>
                        <input
                          id="cns"
                          type="text"
                          v-model="currentDoctor.cns"
                          placeholder="Ex: 123 4567 8901 2345"
                          maxlength="18"
                          required
                          :class="[
                            'w-full px-3 py-2 border rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:border-transparent transition-all',
                            formErrors.cns ? 'border-red-500 ring-2 ring-red-500 ring-opacity-50 bg-red-50 dark:bg-red-900/20' : 'border-gray-200 dark:border-gray-700 focus:ring-blue-500'
                          ]"
                        />
                        <p v-if="formErrors.cns" class="mt-1 text-sm text-red-600 dark:text-red-400 font-medium">{{ formErrors.cns }}</p>
                        <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">Formato: 000 0000 0000 0000</p>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- ABA: Informações Profissionais -->
                <div v-if="activeTab === 'professional'" class="space-y-6">
                  <div>
                    <h3 class="text-lg font-medium mb-4 text-gray-900 dark:text-white">Informações Profissionais</h3>
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label for="crm" class="block text-sm font-medium mb-1">CRM*</label>
                        <input
                          id="crm"
                          type="text"
                          v-model="currentDoctor.crm"
                          placeholder="Ex: 12345"
                          maxlength="10"
                          required
                          :class="[
                            'w-full px-3 py-2 border rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:border-transparent transition-all',
                            formErrors.crm ? 'border-red-500 ring-2 ring-red-500 ring-opacity-50 bg-red-50 dark:bg-red-900/20' : 'border-gray-200 dark:border-gray-700 focus:ring-blue-500'
                          ]"
                        />
                        <p v-if="formErrors.crm" class="mt-1 text-sm text-red-600 dark:text-red-400 font-medium">{{ formErrors.crm }}</p>
                      </div>
                      
                      <div>
                        <label for="crm_state" class="block text-sm font-medium mb-1">Estado do CRM*</label>
                        <select
                          id="crm_state"
                          v-model="currentDoctor.crm_state"
                          required
                          :class="[
                            'w-full px-3 py-2 border rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:border-transparent appearance-none pr-8 transition-all',
                            formErrors.crm_state ? 'border-red-500 ring-2 ring-red-500 ring-opacity-50 bg-red-50 dark:bg-red-900/20' : 'border-gray-200 dark:border-gray-700 focus:ring-blue-500'
                          ]"
                        >
                          <option value="">Selecione o estado</option>
                          <option v-for="state in states" :key="state" :value="state">{{ state }}</option>
                        </select>
                        <p v-if="formErrors.crm_state" class="mt-1 text-sm text-red-600 dark:text-red-400 font-medium">{{ formErrors.crm_state }}</p>
                      </div>
                      
                      <div>
                        <label for="specialty" class="block text-sm font-medium mb-1"> Especialidade*</label>
                        <select
                          id="specialty"
                          v-model="currentDoctor.specialty"
                          required
                          :class="[
                            'w-full px-3 py-2 border rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:border-transparent appearance-none pr-8 transition-all',
                            formErrors.specialty ? 'border-red-500 ring-2 ring-red-500 ring-opacity-50 bg-red-50 dark:bg-red-900/20' : 'border-gray-200 dark:border-gray-700 focus:ring-blue-500'
                          ]"
                        >
                          <option value="">Selecione a Especialidade</option>
                          <option v-for="specialty in specialties" :key="specialty" :value="specialty">{{ specialty }}</option>
                        </select>
                        <p v-if="formErrors.specialty" class="mt-1 text-sm text-red-600 dark:text-red-400 font-medium">{{ formErrors.specialty }}</p>
                      </div>
                      
                      <div>
                        <label for="cbo" class="block text-sm font-medium mb-1">Número CBO</label>
                        <input
                          id="cbo"
                          type="text"
                          v-model="currentDoctor.cbo"
                          maxlength="10"
                          placeholder="Ex: 2251-25"
                          :class="[
                            'w-full px-3 py-2 border rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:border-transparent transition-all',
                            formErrors.cbo ? 'border-red-500 ring-2 ring-red-500 ring-opacity-50 bg-red-50 dark:bg-red-900/20' : 'border-gray-200 dark:border-gray-700 focus:ring-blue-500'
                          ]"
                        />
                        <p v-if="formErrors.cbo" class="mt-1 text-sm text-red-600 dark:text-red-400 font-medium">{{ formErrors.cbo }}</p>
                      </div>
                      
                      <div>
                        <label for="is_active" class="block text-sm font-medium mb-1">Status*</label>
                        <select
                          id="is_active"
                          v-model="currentDoctor.is_active"
                          required
                          class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none pr-8"
                        >
                          <option :value="true">Ativo</option>
                          <option :value="false">Inativo</option>
                        </select>
                      </div>

                      <div>
                        <label for="can_evaluate" class="block text-sm font-medium mb-1">Pode Avaliar*</label>
                        <select
                          id="can_evaluate"
                          v-model="currentDoctor.can_evaluate"
                          required
                          class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none pr-8"
                        >
                          <option :value="true">Sim</option>
                          <option :value="false">Não</option>
                        </select>
                      </div>

                      <div>
                        <label for="can_collect" class="block text-sm font-medium mb-1">Pode Coletar*</label>
                        <select
                          id="can_collect"
                          v-model="currentDoctor.can_collect"
                          required
                          class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none pr-8"
                        >
                          <option :value="true">Sim</option>
                          <option :value="false">Não</option>
                        </select>
                      </div>

                      <div>
                        <label for="can_report" class="block text-sm font-medium mb-1">Pode Laudar*</label>
                        <select
                          id="can_report"
                          v-model="currentDoctor.can_report"
                          required
                          class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none pr-8"
                        >
                          <option :value="true">Sim</option>
                          <option :value="false">Não</option>
                        </select>
                      </div>
                      
                      <div class="md:col-span-2">
                        <label for="observations" class="block text-sm font-medium mb-1">Observações</label>
                        <textarea
                          id="observations"
                          v-model="currentDoctor.observations"
                          rows="3"
                          class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        ></textarea>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- ABA: Endereço -->
                <div v-if="activeTab === 'address'" class="space-y-6">
                  <div>
                    <h3 class="text-lg font-medium mb-4 text-gray-900 dark:text-white">Endereço</h3>
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label for="cep" class="block text-sm font-medium mb-1">CEP</label>
                        <div class="relative">
                          <input
                            id="cep"
                            type="text"
                            v-model="currentDoctor.address.cep"
                            placeholder="00000-000"
                            maxlength="9"
                            :class="[
                              'w-full px-3 py-2 border rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:border-transparent transition-all',
                              formErrors['address.cep'] ? 'border-red-500 ring-2 ring-red-500 ring-opacity-50 bg-red-50 dark:bg-red-900/20' : 'border-gray-200 dark:border-gray-700 focus:ring-blue-500'
                            ]"
                          />
                          <div v-if="isSearchingCEP" class="absolute right-3 top-1/2 -translate-y-1/2">
                            <LoaderIcon class="w-4 h-4 animate-spin text-blue-500" />
                          </div>
                        </div>
                        <p v-if="formErrors['address.cep']" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ formErrors['address.cep'] }}</p>
                        <p v-if="cepError" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ cepError }}</p>
                      </div>
                      
                      <div>
                        <label for="street" class="block text-sm font-medium mb-1">Logradouro</label>
                        <input
                          id="street"
                          type="text"
                          v-model="currentDoctor.address.street"
                          class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                      
                      <div>
                        <label for="number" class="block text-sm font-medium mb-1">Número</label>
                        <input
                          id="number"
                          type="text"
                          v-model="currentDoctor.address.number"
                          class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                      
                      <div>
                        <label for="complement" class="block text-sm font-medium mb-1">Complemento</label>
                        <input
                          id="complement"
                          type="text"
                          v-model="currentDoctor.address.complement"
                          class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                      
                      <div>
                        <label for="neighborhood" class="block text-sm font-medium mb-1">Bairro</label>
                        <input
                          id="neighborhood"
                          type="text"
                          v-model="currentDoctor.address.neighborhood"
                          class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                      
                      <div>
                        <label for="city" class="block text-sm font-medium mb-1">Cidade</label>
                        <input
                          id="city"
                          type="text"
                          v-model="currentDoctor.address.city"
                          :class="[
                            'w-full px-3 py-2 border rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:border-transparent transition-all',
                            formErrors['address.city'] ? 'border-red-500 ring-2 ring-red-500 ring-opacity-50 bg-red-50 dark:bg-red-900/20' : 'border-gray-200 dark:border-gray-700 focus:ring-blue-500'
                          ]"
                        />
                        <p v-if="formErrors['address.city']" class="mt-1 text-sm text-red-600 dark:text-red-400 font-medium">{{ formErrors['address.city'] }}</p>
                      </div>
                      
                      <div>
                        <label for="state" class="block text-sm font-medium mb-1">Estado</label>
                        <select
                          id="state"
                          v-model="currentDoctor.address.state"
                          :class="[
                            'w-full px-3 py-2 border rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:border-transparent appearance-none pr-8 transition-all',
                            formErrors['address.state'] ? 'border-red-500 ring-2 ring-red-500 ring-opacity-50 bg-red-50 dark:bg-red-900/20' : 'border-gray-200 dark:border-gray-700 focus:ring-blue-500'
                          ]"
                        >
                          <option value="">Selecione o estado</option>
                          <option v-for="state in states" :key="state" :value="state">{{ state }}</option>
                        </select>
                        <p v-if="formErrors['address.state']" class="mt-1 text-sm text-red-600 dark:text-red-400 font-medium">{{ formErrors['address.state'] }}</p>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- ABA: Assinatura -->
                <div v-if="activeTab === 'signature'" class="space-y-6">
                  <div>
                    <h3 class="text-lg font-medium mb-4 text-gray-900 dark:text-white">Assinatura Médica</h3>
                    
                    <!-- Informações sobre upload -->
                    <div class="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-700">
                      <div class="flex items-start gap-3">
                        <InfoIcon class="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                        <div>
                          <h4 class="text-sm font-medium text-blue-900 dark:text-blue-100 mb-1">Requisitos para Upload</h4>
                          <ul class="text-xs text-blue-800 dark:text-blue-200 space-y-1">
                            <li>• Formato aceito: <strong>.bmp</strong> (bitmap)</li>
                            <li>• Tamanho máximo: <strong>2MB</strong></li>
                            <li>• Resolução recomendada: <strong>300 DPI</strong></li>
                            <li>• Fundo transparente ou branco preferível</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                    
                    <div class="space-y-6">
                      <!-- Preview da Assinatura (quando existe) -->
                      <div v-if="currentDoctor.medical_signature" class="w-full">
                        <div class="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border-2 border-green-200 dark:border-green-700 rounded-xl p-6 shadow-sm">
                          <div class="flex justify-between items-center mb-4">
                            <div class="flex items-center gap-2">
                              <div class="w-8 h-8 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                                <CheckIcon class="w-4 h-4 text-green-600 dark:text-green-400" />
                              </div>
                              <span class="text-sm font-medium text-green-900 dark:text-green-100">Assinatura Carregada</span>
                            </div>
                            <button
                              @click="removeSignature"
                              class="flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                              type="button"
                              title="Remover assinatura"
                            >
                              <TrashIcon class="w-4 h-4" />
                              <span>Remover</span>
                            </button>
                          </div>
                          
                          <!-- Preview da imagem -->
                          <div class="bg-white dark:bg-gray-900 rounded-lg border-2 border-dashed border-green-200 dark:border-green-700 p-6">
                            <div class="flex justify-center">
                              <img
                                :src="getSignatureDataUrl()"
                                alt="Assinatura Médica"
                                class="max-w-full max-h-40 object-contain drop-shadow-sm"
                              />
                            </div>
                          </div>
                          
                          <!-- Informações do arquivo -->
                          <div class="mt-4 text-xs text-green-700 dark:text-green-300 text-center">
                            <span class="inline-flex items-center gap-1 px-2 py-1 bg-green-100 dark:bg-green-900/30 rounded-full">
                              <CheckIcon class="w-3 h-3" />
                              Arquivo válido carregado
                            </span>
                          </div>
                        </div>
                      </div>
                      
                                             <!-- Área de Upload (quando não existe assinatura) -->
                       <div v-else class="w-full">
                         <div 
                           class="upload-area relative border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl p-8 text-center hover:border-blue-400 dark:hover:border-blue-500 hover:bg-blue-50/50 dark:hover:bg-blue-900/10 transition-all duration-200 cursor-pointer group"
                           @click="triggerFileInput"
                           @dragover.prevent="onDragOver"
                           @dragleave.prevent="onDragLeave"
                           @drop.prevent="onDrop"
                           :class="[
                             isDragOver ? 'border-blue-400 dark:border-blue-500 bg-blue-50/50 dark:bg-blue-900/10 drag-over' : 'signature-gradient'
                           ]"
                         >
                                                     <!-- Ícone animado -->
                           <div class="mb-4">
                             <div class="upload-icon w-16 h-16 mx-auto rounded-full bg-gradient-to-br from-blue-100 to-indigo-100 dark:from-blue-900/30 dark:to-indigo-900/30 flex items-center justify-center group-hover:scale-110 transition-transform duration-200">
                               <SignatureIcon class="w-8 h-8 text-blue-600 dark:text-blue-400" />
                             </div>
                           </div>
                          
                          <!-- Texto principal -->
                          <div class="space-y-2">
                            <h4 class="text-lg font-medium text-gray-900 dark:text-white">
                              {{ isDragOver ? 'Solte o arquivo aqui' : 'Upload da Assinatura' }}
                            </h4>
                            <p class="text-sm text-gray-600 dark:text-gray-400">
                              {{ isDragOver ? 'Solte o arquivo .bmp para fazer upload' : 'Clique para selecionar ou arraste o arquivo .bmp aqui' }}
                            </p>
                          </div>
                          
                          <!-- Botão de upload -->
                          <div class="mt-6">
                            <span class="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors shadow-sm group-hover:shadow-md">
                              <PlusIcon class="w-4 h-4" />
                              Selecionar Arquivo
                            </span>
                          </div>
                          
                          <!-- Especificações técnicas -->
                          <div class="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                            <div class="flex justify-center space-x-6 text-xs text-gray-500 dark:text-gray-400">
                              <span class="flex items-center gap-1">
                
                                .BMP apenas
                              </span>
                              <span class="flex items-center gap-1">
                                
                               |   Máx. 2MB
                              </span>
                              <span class="flex items-center gap-1">
                                
                               |   300 DPI ideal
                              </span>
                            </div>
                          </div>
                          
                          <!-- Input file escondido -->
                          <input
                            id="signature-upload"
                            ref="fileInput"
                            type="file"
                            accept=".bmp"
                            @change="handleSignatureUpload"
                            class="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                            tabindex="-1"
                          />
                        </div>
                      </div>
                      
                      <!-- Dicas adicionais -->
                      <div class="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg p-4">
                        <div class="flex items-start gap-3">
                          <WarningIcon class="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
                          <div>
                            <h4 class="text-sm font-medium text-amber-900 dark:text-amber-100 mb-1">Dicas para melhor qualidade</h4>
                            <ul class="text-xs text-amber-800 dark:text-amber-200 space-y-1">
                              <li>• Escaneie a assinatura em alta resolução (300 DPI ou superior)</li>
                              <li>• Use caneta preta ou azul escura para melhor contraste</li>
                              <li>• Certifique-se de que a assinatura está centralizada na imagem</li>
                              <li>• Evite fundos coloridos ou com texturas</li>
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>



                <!-- Form Actions -->
                <div class="mt-8 flex justify-between items-center">
                  <div class="flex gap-2">
                    <!-- Navigation buttons could go here -->
                  </div>
                  <div class="flex gap-3">
                    <button
                      class="px-4 py-2 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-md transition-colors"
                      @click="goToList"
                    >
                      Cancelar
                    </button>
                    <button
                      class="clinic-btn text-white rounded-md transition-colors shadow-sm disabled:bg-gray-300 dark:disabled:bg-gray-600 disabled:cursor-not-allowed"
                      @click="saveDoctor"
                      :disabled="!isFormValid || isSaving"
                    >
                      {{ isSaving ? 'Salvando...' : (editMode ? 'Atualizar' : 'Salvar') }} Médico
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Success Modal -->
          <div
            class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            v-if="showSuccessModal"
            @click.self="closeSuccessModal"
          >
            <div
              class="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto animate-fadeIn"
            >
              <div class="p-6">
                <div class="text-center mb-6">
                  <div
                    class="w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center text-green-600 dark:text-green-400 mx-auto mb-4"
                  >
                    <CheckIcon class="w-8 h-8" />
                  </div>
                  <h3 class="text-xl font-medium mb-2">{{ editMode ? 'Médico Atualizado' : 'Médico Cadastrado' }}</h3>
                  <p class="text-gray-600 dark:text-gray-400">
                    {{ editMode ? 'O médico foi atualizado com sucesso.' : 'O médico foi cadastrado com sucesso no sistema.' }}
                  </p>
                </div>

                <!-- Credenciais do Usuário (apenas para novos médicos) -->
                <div v-if="!editMode && userCredentials" class="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-700">
                  <div class="flex items-center justify-between mb-3">
                    <h4 class="text-lg font-medium text-blue-900 dark:text-blue-100 flex items-center gap-2">
                      <UsersIcon class="w-5 h-5" />
                      {{ isNewUser ? 'Credenciais do Usuário Criado' : 'Usuário Associado' }}
                    </h4>
                    <button
                      v-if="isNewUser"
                      @click="copyCredentials"
                      class="px-3 py-2 rounded-md transition-colors flex items-center gap-2 text-sm font-medium"
                      :class="copyFeedback ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400' : 'text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-200 hover:bg-blue-100 dark:hover:bg-blue-800/30'"
                      title="Copiar credenciais para área de transferência"
                    >
                      <CheckIcon v-if="copyFeedback === 'Credenciais copiadas!'" class="w-4 h-4" />
                      <AlertTriangleIcon v-else-if="copyFeedback === 'Erro ao copiar'" class="w-4 h-4 text-red-500" />
                      <CopyIcon v-else class="w-4 h-4" />
                      <span>{{ copyFeedback || 'Copiar' }}</span>
                    </button>
                  </div>
                  
                  <div class="space-y-2 text-sm">
                    <div class="flex justify-between">
                      <span class="font-medium text-gray-700 dark:text-gray-300">Usuário:</span>
                      <span class="font-mono bg-white dark:bg-gray-800 px-2 py-1 rounded border">{{ userCredentials.username }}</span>
                    </div>
                    <div class="flex justify-between">
                      <span class="font-medium text-gray-700 dark:text-gray-300">
                        {{ isNewUser ? 'Senha Temporária:' : 'Status:' }}
                      </span>
                      <span 
                        class="font-mono px-2 py-1 rounded border"
                        :class="isNewUser ? 'bg-white dark:bg-gray-800' : 'bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-400'"
                      >
                        {{ userCredentials.temporary_password }}
                      </span>
                    </div>
                    <div class="flex justify-between">
                      <span class="font-medium text-gray-700 dark:text-gray-300">Email:</span>
                      <span class="font-mono bg-white dark:bg-gray-800 px-2 py-1 rounded border">{{ userCredentials.email }}</span>
                    </div>
                                      <div class="flex justify-between">
                    <span class="font-medium text-gray-700 dark:text-gray-300">Grupos:</span>
                    <span class="text-xs bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400 px-2 py-1 rounded">{{ userCredentials.groups.join(', ') }}</span>
                  </div>
                  <div class="flex justify-between">
                    <span class="font-medium text-gray-700 dark:text-gray-300">E-mail enviado:</span>
                    <span 
                      class="text-xs px-2 py-1 rounded"
                      :class="userCredentials.email_sent ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400' : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-400'"
                    >
                      {{ userCredentials.email_sent ? 'Sim' : 'Não' }}
                    </span>
                  </div>
                  </div>
                  
                  <div 
                    class="mt-3 p-3 rounded"
                    :class="isNewUser ? 'bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700' : 'bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-700'"
                  >
                    <div class="flex items-start gap-2">
                      <template v-if="isNewUser">
                        <WarningIcon class="w-4 h-4 text-yellow-600 dark:text-yellow-400 mt-0.5 flex-shrink-0" />
                        <p class="text-xs text-yellow-800 dark:text-yellow-200">
                          <strong>IMPORTANTE:</strong> Anote essas credenciais e forneça ao médico para o primeiro acesso. 
                          Ele deverá alterar a senha no primeiro login.
                        </p>
                      </template>
                      <template v-else>
                        <InfoIcon class="w-4 h-4 text-orange-600 dark:text-orange-400 mt-0.5 flex-shrink-0" />
                        <p class="text-xs text-orange-800 dark:text-orange-200">
                          <strong>INFORMAÇÃO:</strong> Este médico foi associado a um usuário existente no sistema. 
                          As credenciais originais permanecem as mesmas.
                        </p>
                      </template>
                    </div>
                  </div>
                  
                  <!-- Informação sobre envio de e-mail -->
                  <div 
                    class="mt-3 p-3 rounded"
                    :class="userCredentials.email_sent ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700' : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700'"
                  >
                    <div class="flex items-start gap-2">
                      <template v-if="userCredentials.email_sent">
                        <CheckIcon class="w-4 h-4 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                        <p class="text-xs text-green-800 dark:text-green-200">
                          <strong>E-MAIL ENVIADO:</strong> As credenciais foram enviadas automaticamente para o e-mail do médico.
                        </p>
                      </template>
                      <template v-else>
                        <AlertTriangleIcon class="w-4 h-4 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
                        <p class="text-xs text-red-800 dark:text-red-200">
                          <strong>E-MAIL NÃO ENVIADO:</strong> Houve um problema no envio do e-mail. As credenciais devem ser fornecidas manualmente.
                        </p>
                      </template>
                    </div>
                  </div>
                </div>

                <div class="flex justify-center gap-3">
                  <button
                    class="clinic-btn text-white rounded-md transition-colors shadow-sm"
                    @click="closeSuccessModal"
                  >
                    {{ userCredentials ? 'Entendido' : 'OK' }}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Delete Confirmation Modal -->
          <div
            class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            v-if="showDeleteModal"
            @click.self="showDeleteModal = false"
          >
            <div
              class="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto animate-fadeIn"
            >
              <div class="p-6">
                <div
                  class="w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center text-red-600 dark:text-red-400 mx-auto mb-4"
                >
                  <AlertTriangleIcon class="w-8 h-8" />
                </div>
                <h3 class="text-xl font-medium mb-2 text-center">Confirmar Exclusão</h3>
                <p class="text-gray-600 dark:text-gray-400 mb-6 text-center">
                  Tem certeza que deseja excluir o médico <span class="font-medium">{{ doctorToDelete?.name }}</span>?
                  <br />Esta ação não pode ser desfeita.
                </p>
                <div class="flex justify-end gap-3">
                  <button
                    class="px-4 py-2 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-md transition-colors"
                    @click="showDeleteModal = false"
                  >
                    Cancelar
                  </button>
                  <button
                    class="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-md transition-colors shadow-sm"
                    @click="deleteDoctor"
                    :disabled="isDeleting"
                  >
                    {{ isDeleting ? 'Excluindo...' : 'Excluir' }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </template>
</BaseTemplate>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import BaseTemplate from '../templates/BaseTemplate.vue';
import apiClient from '@/services/authService';
import {
  validateDoctorForm,
  applyCPFMask,
  applyPhoneMask,
  applyCEPMask,
  applyCRMMask,
  applyCNSMask,
  fetchAddressByCEP,
  formatCRM
} from '@/utils/validations';
import {
  Plus as PlusIcon,
  ChevronDown as ChevronDownIcon,
  Check as CheckIcon,
  Edit as EditIcon,
  Trash as TrashIcon,
  Save as SaveIcon,
  ArrowLeft as ArrowLeftIcon,
  RefreshCw as RefreshCwIcon,
  Search as SearchIcon,
  Loader as LoaderIcon,
  AlertTriangle as AlertTriangleIcon,
  User as UserIcon,
  Briefcase as BriefcaseIcon,
  MapPin as MapPinIcon,
  PenTool as SignatureIcon,
  Copy as CopyIcon,
  AlertCircle as WarningIcon,
  Info as InfoIcon,
  Users as UsersIcon
} from 'lucide-vue-next';

// Estado de exibição
const currentView = ref('list');
const editMode = ref(false);
const showSuccessModal = ref(false);
const showDeleteModal = ref(false);
const doctorToDelete = ref(null);
const searchQuery = ref('');
const filterStatus = ref('');
const filterSpecialty = ref('');
const activeTab = ref('personal');
const currentPage = ref(1);
const pageSize = ref(10);
const totalCount = ref(0);
const totalPages = ref(0);
const isLoading = ref(false);
const isSaving = ref(false);
const isDeleting = ref(false);
const errorMessage = ref('');
const userCredentials = ref(null);
const isDragOver = ref(false);
const fileInput = ref(null);

// Estados para validação
const formErrors = ref({});
const isSearchingCEP = ref(false);
const cepError = ref('');

// Debounce para busca
let searchTimeout = null;

// Abas do formulário
const tabs = [
  { id: 'personal', name: 'Pessoal', icon: UserIcon },
  { id: 'professional', name: 'Profissional', icon: BriefcaseIcon },
  { id: 'address', name: 'Endereço', icon: MapPinIcon },
  { id: 'signature', name: 'Assinatura', icon: SignatureIcon }
];

// Estados brasileiros
const states = [
  'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG', 
  'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'
];

// Especialidades médicas
const specialties = [
  'Cardiologia', 'Dermatologia', 'Endocrinologia', 'Gastroenterologia', 'Ginecologia',
  'Neurologia', 'Oftalmologia', 'Ortopedia', 'Pediatria', 'Psiquiatria', 'Radiologia',
  'Urologia', 'Anestesiologia', 'Cirurgia Geral', 'Clínica Médica', 'Medicina do Trabalho',
  'Medicina de Família', 'Oncologia', 'Pneumologia', 'Reumatologia'
];

// Mapeamento de especialidades para CBO
const specialtyCBOMap = {
  'Cardiologia': '225120',
  'Dermatologia': '225135',
  'Endocrinologia': '225155',
  'Gastroenterologia': '225165',
  'Ginecologia': '225250',
  'Neurologia': '225112',
  'Oftalmologia': '225265',
  'Ortopedia': '225270',
  'Pediatria': '225124',
  'Psiquiatria': '225133',
  'Radiologia': '225320',
  'Urologia': '225285',
  'Anestesiologia': '225151',
  'Cirurgia Geral': '225225',
  'Clínica Médica': '225125',
  'Medicina do Trabalho': '225140',
  'Medicina de Família': '225130',
  'Oncologia': '225121',
  'Pneumologia': '225127',
  'Reumatologia': '225136'
};

// Modelo vazio de médico
const emptyDoctor = {
  id: null,
  name: '',
  email: '',
  phone: '',
  birth_date: '',
  cpf: '',
  cns: '',
  crm: '',
  crm_state: '',
  specialty: '',
  cbo: '',
  is_active: true,
  can_evaluate: false,
  can_collect: false,
  can_report: false,
  observations: '',
  medical_signature: '',
  address: {
    cep: '',
    street: '',
    number: '',
    complement: '',
    neighborhood: '',
    city: '',
    state: ''
  }
};

// Dados reativos
const doctors = ref([]);
const currentDoctor = ref({ ...emptyDoctor });

// Watchers para validação e máscaras
watch(() => currentDoctor.value.cpf, (newValue) => {
  if (newValue) {
    currentDoctor.value.cpf = applyCPFMask(newValue);
  }
});

watch(() => currentDoctor.value.cns, (newValue) => {
  if (newValue) {
    currentDoctor.value.cns = applyCNSMask(newValue);
  }
});

watch(() => currentDoctor.value.crm, (newValue) => {
  if (newValue) {
    currentDoctor.value.crm = applyCRMMask(newValue);
  }
});

watch(() => currentDoctor.value.phone, (newValue) => {
  if (newValue) {
    currentDoctor.value.phone = applyPhoneMask(newValue);
  }
});

watch(() => currentDoctor.value.address?.cep, (newValue) => {
  if (newValue) {
    currentDoctor.value.address.cep = applyCEPMask(newValue);
    
    // Buscar endereço quando CEP estiver completo
    if (newValue.replace(/\D/g, '').length === 8) {
      searchAddressByCEP();
    }
  }
});

// Watch para preencher CBO automaticamente quando especialidade for selecionada
watch(() => currentDoctor.value.specialty, (newSpecialty) => {
  if (newSpecialty && specialtyCBOMap[newSpecialty]) {
    currentDoctor.value.cbo = specialtyCBOMap[newSpecialty];
  } else {
    currentDoctor.value.cbo = '';
  }
});
// Adicione esta função dentro de <script setup>

function validateField(fieldName) {
  // Roda a validação completa para obter todos os erros possíveis
  const allErrors = validateDoctorForm(currentDoctor.value);

  // Verifica se existe um erro específico para o campo que estamos validando
  if (allErrors[fieldName]) {
    // Se existe, adiciona a mensagem de erro ao nosso objeto reativo `formErrors`
    formErrors.value[fieldName] = allErrors[fieldName];
  } else {
    // Se não existe mais erro para este campo (o usuário corrigiu), remove a chave do objeto
    delete formErrors.value[fieldName];
  }
}
// Função para buscar endereço por CEP
async function searchAddressByCEP() {
  if (!currentDoctor.value.address?.cep) return;
  
  isSearchingCEP.value = true;
  cepError.value = '';
  
  try {
    const cep = currentDoctor.value.address.cep.replace(/\D/g, '');
    const addressData = await fetchAddressByCEP(cep);
    
    if (addressData) {
      currentDoctor.value.address.state = addressData.uf;
      currentDoctor.value.address.city = addressData.localidade;
      currentDoctor.value.address.street = addressData.logradouro;
      currentDoctor.value.address.neighborhood = addressData.bairro;
      
      // Tentar inferir o tipo de logradouro
      if (addressData.logradouro) {
        const logradouro = addressData.logradouro.toLowerCase();
        if (logradouro.includes('rua')) {
          currentDoctor.value.address.street_type = 'Rua';
        } else if (logradouro.includes('avenida') || logradouro.includes('av.')) {
          currentDoctor.value.address.street_type = 'Avenida';
        } else if (logradouro.includes('travessa')) {
          currentDoctor.value.address.street_type = 'Travessa';
        } else if (logradouro.includes('praça')) {
          currentDoctor.value.address.street_type = 'Praça';
        }
      }
    }
  } catch (error) {
    console.error('Erro ao buscar CEP:', error);
    cepError.value = 'Erro ao buscar endereço. Verifique o CEP informado.';
  } finally {
    isSearchingCEP.value = false;
  }
}

// Computed properties
const isFormValid = computed(() => {
  // A validade do formulário agora é simplesmente verificar se não há erros.
  return Object.keys(formErrors.value).length === 0;
});


const isNewUser = computed(() => {
  return userCredentials.value && 
         userCredentials.value.temporary_password && 
         !userCredentials.value.temporary_password.includes('Usuário já existia');
});

// Métodos de API
async function loadDoctors(page = 1) {
  isLoading.value = true;
  errorMessage.value = '';

  try {
    const params = {
      page,
      page_size: pageSize.value
    };
    
    if (searchQuery.value.trim()) {
      params.search = searchQuery.value.trim();
    }
    
    if (filterStatus.value) {
      params.is_active = filterStatus.value;
    }
    
    if (filterSpecialty.value) {
      params.specialty = filterSpecialty.value;
    }
    
    const response = await apiClient.get('/api/v1/doctors/list/', { params });
    
    doctors.value = response.data.results;
    totalCount.value = response.data.count;
    totalPages.value = response.data.total_pages;
    currentPage.value = page;
    
  } catch (error) {
    console.error('Erro ao carregar médicos:', error);
    errorMessage.value = 'Erro ao carregar médicos. Tente novamente.';
    
    if (error.response?.status === 401) {
      errorMessage.value = 'Sessão expirada. Faça login novamente.';
    }
  } finally {
    isLoading.value = false;
  }
}

async function saveDoctor() {
  // 1. Roda a validação completa e atualiza a UI com TODOS os erros
  formErrors.value = validateDoctorForm(currentDoctor.value);

  // 2. Se o formulário NÃO for válido (verificando o resultado da validação), para a execução.
  if (!isFormValid.value) {
    errorMessage.value = 'Por favor, corrija os erros destacados no formulário.';
    return;
  }
  
  // Se for válido, continua com o processo de salvar...
  if (isSaving.value) return;

  isSaving.value = true;
  errorMessage.value = '';
  userCredentials.value = null;

  try {
    const doctorData = { ...currentDoctor.value };
    
    if (editMode.value) {
      await apiClient.put(`/api/v1/doctors/update/${doctorData.id}/`, doctorData);
    } else {
      const response = await apiClient.post('/api/v1/doctors/add/', doctorData);
      userCredentials.value = response.data.user_credentials || null;
    }
    
    showSuccessModal.value = true;
    await loadDoctors(editMode.value ? currentPage.value : 1);
  } catch (error) {
    console.error('Erro ao salvar médico:', error);
    errorMessage.value = 'Erro ao salvar médico. Verifique os dados e tente novamente.';
  } finally {
    isSaving.value = false;
  }
}


async function deleteDoctor() {
  if (!doctorToDelete.value || isDeleting.value) return;

  isDeleting.value = true;

  try {
    await apiClient.delete(`/api/v1/doctors/delete/${doctorToDelete.value.id}/`);
    
    showDeleteModal.value = false;
    doctorToDelete.value = null;
    
    // Atualiza a lista
    await loadDoctors(currentPage.value);
    
  } catch (error) {
    console.error('Erro ao excluir médico:', error);
    
    if (error.response?.status === 401) {
      errorMessage.value = 'Sessão expirada. Faça login novamente.';
    } else {
      errorMessage.value = 'Erro ao excluir médico. Tente novamente.';
    }
  } finally {
    isDeleting.value = false;
  }
}

async function fetchDoctorDetails(doctorId) {
  try {
    const response = await apiClient.get(`/api/v1/doctors/get/${doctorId}/`);
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar detalhes do médico:', error);
    errorMessage.value = 'Erro ao buscar detalhes do médico.';
    return null;
  }
}

// Métodos de controle da UI
function openNewDoctorForm() {
  currentDoctor.value = JSON.parse(JSON.stringify(emptyDoctor));
  editMode.value = false;
  activeTab.value = 'personal';
  currentView.value = 'form';
  errorMessage.value = '';
  formErrors.value = {};
  cepError.value = '';
}

async function editDoctor(doctor) {
  try {
    isLoading.value = true;
    // Buscar detalhes completos do médico
    const doctorDetails = await fetchDoctorDetails(doctor.id);
    
    if (doctorDetails) {
      currentDoctor.value = doctorDetails;
      
      // Garantir que o objeto address existe
      if (!currentDoctor.value.address) {
        currentDoctor.value.address = { ...emptyDoctor.address };
      }
      
      editMode.value = true;
      activeTab.value = 'personal';
      currentView.value = 'form';
      formErrors.value = {};
      cepError.value = '';
    }
  } catch (error) {
    console.error('Erro ao editar médico:', error);
    errorMessage.value = 'Erro ao carregar dados do médico para edição.';
  } finally {
    isLoading.value = false;
  }
}

function goToList() {
  currentView.value = 'list';
  errorMessage.value = '';
  formErrors.value = {};
  cepError.value = '';
}

function confirmDeleteDoctor(doctor) {
  doctorToDelete.value = doctor;
  showDeleteModal.value = true;
}

function resetFilters() {
  searchQuery.value = '';
  filterStatus.value = '';
  filterSpecialty.value = '';
  loadDoctors(1);
}

function closeSuccessModal() {
  showSuccessModal.value = false;
  userCredentials.value = null;
  currentView.value = 'list';
}

// Debounce para busca
function debouncedSearch() {
  if (searchTimeout) {
    clearTimeout(searchTimeout);
  }
  
  searchTimeout = setTimeout(() => {
    loadDoctors(1);
  }, 500);
}

// Métodos para assinatura
function handleSignatureUpload(event) {
  const file = event.target.files[0];
  
  if (!file) return;
  
  processSignatureFile(file);
}

function processSignatureFile(file) {
  // Verificar se é um arquivo BMP
  if (!file.type.includes('bmp') && !file.name.toLowerCase().endsWith('.bmp')) {
    errorMessage.value = 'Por favor, selecione apenas arquivos .bmp para a assinatura médica.';
    return;
  }
  
  // Verificar tamanho do arquivo (máximo 2MB)
  if (file.size > 2 * 1024 * 1024) {
    errorMessage.value = 'A assinatura deve ter no máximo 2MB.';
    return;
  }
  
  const reader = new FileReader();
  reader.onload = function(e) {
    // Converter para base64 (remover o prefixo data:image/bmp;base64,)
    const base64String = e.target.result.split(',')[1];
    currentDoctor.value.medical_signature = base64String;
    errorMessage.value = '';
  };
  
  reader.onerror = function() {
    errorMessage.value = 'Erro ao carregar a assinatura. Tente novamente.';
  };
  
  reader.readAsDataURL(file);
}

function removeSignature() {
  currentDoctor.value.medical_signature = '';
  // Limpar o input file
  if (fileInput.value) {
    fileInput.value.value = '';
  }
}

function getSignatureDataUrl() {
  if (!currentDoctor.value.medical_signature) return '';
  return `data:image/bmp;base64,${currentDoctor.value.medical_signature}`;
}

// Métodos para drag and drop
function onDragOver(event) {
  event.preventDefault();
  isDragOver.value = true;
}

function onDragLeave(event) {
  event.preventDefault();
  isDragOver.value = false;
}

function onDrop(event) {
  event.preventDefault();
  isDragOver.value = false;
  
  const files = event.dataTransfer.files;
  if (files.length > 0) {
    processSignatureFile(files[0]);
  }
}

function triggerFileInput() {
  if (fileInput.value) {
    fileInput.value.click();
  }
}

// Função de formatação para exibição
function formatCRMDisplay(crm) {
  return formatCRM(crm);
}

// Estado para feedback de cópia
const copyFeedback = ref('');

// Função para copiar credenciais para área de transferência
function copyCredentials() {
  if (!userCredentials.value || !isNewUser.value) return;
  
  const emailStatus = userCredentials.value.email_sent ? 'ENVIADO' : 'NÃO ENVIADO';
  
  const credentials = `Credenciais de Acesso
Usuário: ${userCredentials.value.username}
Senha Temporária: ${userCredentials.value.temporary_password}
Email: ${userCredentials.value.email}
Grupos: ${userCredentials.value.groups.join(', ')}
Status do E-mail: ${emailStatus}

IMPORTANTE: O médico deve alterar a senha no primeiro acesso.`;
  
  navigator.clipboard.writeText(credentials).then(() => {
    copyFeedback.value = 'Credenciais copiadas!';
    setTimeout(() => {
      copyFeedback.value = '';
    }, 2000);
  }).catch(err => {
    console.error('Erro ao copiar credenciais:', err);
    copyFeedback.value = 'Erro ao copiar';
    setTimeout(() => {
      copyFeedback.value = '';
    }, 2000);
  });
}

// Lifecycle
onMounted(async () => {
  await loadDoctors(1);
  currentView.value = 'list';
});
</script>

<style>
@import 'tailwindcss/base';
@import 'tailwindcss/components';
@import 'tailwindcss/utilities';

.clinic-btn {
  background-color: var(--clinic-btn-bg);
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 0.375rem;
  font-weight: 500;
  transition: all 0.2s ease-in-out;
  border: none;
  cursor: pointer;
}

.clinic-btn:hover:not(:disabled) {
  background-color: var(--clinic-btn-bg-hover);
  transform: translateY(-1px);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

.clinic-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.animate-fadeIn {
  animation: fadeIn 0.3s ease-in-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Animações adicionais para a área de upload */
.upload-area {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.upload-area:hover {
  transform: translateY(-1px);
}

.upload-icon {
  transition: transform 0.2s ease-in-out;
}

.group:hover .upload-icon {
  transform: scale(1.05);
}

/* Efeitos de gradiente suaves */
.signature-gradient {
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
}

.dark .signature-gradient {
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(99, 102, 241, 0.1) 100%);
}

/* Animação para o drag over */
.drag-over {
  animation: dragPulse 1s ease-in-out infinite;
}

@keyframes dragPulse {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.02);
  }
}
</style>